import { useMemo } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { EditableToken, useCollectionBinding, useContentToken, useProperty } from '@vibesites/runtime/react';
import pagesData from '../pages.json';
import { useCtaColorVars } from '../hooks/useCtaColorVars';
import { useLocalizedSpelling } from '../hooks/useLocalizedSpelling';

interface PageEntry {
  name: string;
  path: string;
  position: number;
  visible?: boolean;
}

interface VirtualTour {
  virtual_tour_url?: string | null;
}

interface Review {
  text?: string | null;
}

interface FaqItem {
  question?: string | null;
}

export function SitemapContent() {
  const heading = useContentToken('sitemapcontent.heading');
  const virtualTours = useCollectionBinding<VirtualTour>('sitemapcontent.virtual_tours', { collection: 'virtual_tours' });
  const hasVirtualTours = (virtualTours || []).some((t) => t.virtual_tour_url);
  const reviews = useCollectionBinding<Review>('sitemapcontent.reviews', { collection: 'reviews' });
  const hasReviews = (reviews || []).some((r) => r.text && String(r.text).trim());
  const faqs = useCollectionBinding<FaqItem>('sitemapcontent.faqs', { collection: 'faqs' });
  const hasFaqs = (faqs || []).some((item) => {
    if (!item.question) return false;
    const text = String(item.question)
      .replace(/<[^>]*>/g, '')
      .replace(/&nbsp;/gi, '')
      .trim();
    return text !== '';
  });
  const property = useProperty() as Record<string, unknown>;
  const engrainUrl = property?.custom_engrain_sightmap_url;
  const hasInteractiveMap = !!(engrainUrl && String(engrainUrl).trim());
  const ctaColorVars = useCtaColorVars();
  const localize = useLocalizedSpelling();

  const pages = useMemo(
    () =>
      Object.values(pagesData as Record<string, PageEntry>)
        .filter((p) => {
          if (p.visible === false) return false;
          if (p.path === '/sitemap' || p.path.includes('{{')) return false;
          if (p.path === '/virtual-tour' && !hasVirtualTours) return false;
          if (p.path === '/reviews' && !hasReviews) return false;
          if (p.path === '/interactive-map' && !hasInteractiveMap) return false;
          if (p.path === '/frequently-asked-questions' && !hasFaqs) return false;
          return true;
        })
        .sort((a, b) => a.position - b.position),
    [hasVirtualTours, hasReviews, hasInteractiveMap, hasFaqs],
  );

  return (
    <section className="py-16 md:py-24 bg-[var(--vibe-color-background)]" style={ctaColorVars}>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <EditableToken
          path="sitemapcontent.heading"
          as="h1"
          className="sr-only"
        >
          {heading}
        </EditableToken>
        <ul className="flex flex-col items-center gap-4">
          {pages.map((page) => (
            <li key={page.path} className="flex flex-col items-center">
              <a
                href={page.path}
                className="font-body text-[18px] tracking-wide text-[var(--vibe-color-text)] hover:text-[var(--cta-bg,var(--vibe-color-link-hover))] transition-colors"
              >
                {localize(page.name)}
              </a>
              <span className="mt-2 block h-px w-16 bg-[var(--vibe-color-text)] opacity-40" aria-hidden="true" />
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'SitemapContent',
  displayName: 'Sitemap',
  tokens: {
    'sitemapcontent.heading': {
      label: 'Page Heading',
      type: 'text',
      defaultValue: 'Sitemap',
    },
  },
  collectionBindings: {
    'sitemapcontent.virtual_tours': {
      label: 'Virtual Tours',
      collection: 'virtual_tours',
    },
    'sitemapcontent.reviews': {
      label: 'Reviews',
      collection: 'reviews',
    },
    'sitemapcontent.faqs': {
      label: 'FAQs',
      collection: 'faqs',
    },
  },
};

(SitemapContent as any).contentSchema = contentSchema;

export default SitemapContent;
